'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { CONTACT_PHONE, CONTACT_PHONE_TEL, INSTAGRAM_DM_URL } from '@/lib/policies';
import { CheckoutError, startCheckout } from '@/lib/checkout';
import { trackBeginCheckout } from '@/lib/analytics';
import type { CartLine } from '@/lib/cart-store';

/**
 * Checkout button state: pending flag, shopper-safe error, and the handler.
 * Used by the cart page and the cart drawer.
 */
export function useCheckout(lines: CartLine[]) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<{ message: string; variantIds: string[] } | null>(null);

  // A synchronous lock: two clicks in the same tick must not start two checkouts.
  const inFlight = useRef(false);

  // Back from Stripe via the back/forward cache restores this page with the
  // button still on "Redirecting…". Un-stick it.
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        inFlight.current = false;
        setPending(false);
      }
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  const go = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError(null);
    setPending(true);
    trackBeginCheckout(lines.filter((l) => !l.unavailable).map((l) => ({
      item_id: l.productId,
      item_name: l.name,
      item_brand: l.brand,
      item_variant: l.variantTitle ?? undefined,
      price: l.priceCents / 100,
      quantity: l.qty,
    })));
    try {
      await startCheckout(lines);
      // Redirect issued; leave the button pending while the page unloads.
    } catch (err) {
      inFlight.current = false;
      setPending(false);
      setError(
        err instanceof CheckoutError
          ? { message: err.message, variantIds: err.unavailableVariantIds }
          : { message: 'We could not start checkout just now.', variantIds: [] },
      );
    }
  }, [lines]);

  return { pending, error, go };
}

/** Branded error: never a raw platform message, always a way to order anyway. */
export function CheckoutFallback({ message }: { message: string }) {
  return (
    <div role="alert" className="text-sm text-ink bg-bone border border-ink/25 px-4 py-3 mb-4 leading-relaxed">
      <p className="mb-2">{message}</p>
      <p className="text-ink/75">
        You can still order with us directly: call or text{' '}
        <a href={CONTACT_PHONE_TEL} className="underline underline-offset-2 font-numerals">{CONTACT_PHONE}</a>, or{' '}
        <a href={INSTAGRAM_DM_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">message us on Instagram</a>.
      </p>
    </div>
  );
}
